import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
     dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "realestate-solution" },
      { name: "Logistics", link: "logistics-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏦",
    title: "Digital Banking Platform",
    desc: "Full-stack neobank and digital banking platforms — account onboarding, KYC/AML, multi-currency wallets, transaction ledgers, open banking APIs, and real-time balance dashboards built for RBI-compliant operations.",
    tags: ["KYC/AML", "Open Banking", "Multi-Currency", "RBI Compliant"],
    color: "#4F46E5",
  },
  {
    icon: "💳",
    title: "Payment Gateway & Processing",
    desc: "PCI-DSS Level 1 payment processing engines with multi-rail support (UPI, NEFT, RTGS, IMPS, cards), smart routing, retry logic, reconciliation, and real-time settlement dashboards.",
    tags: ["PCI-DSS", "UPI", "Razorpay", "Stripe"],
    color: "#0891B2",
  },
  {
    icon: "🤖",
    title: "AI Fraud Detection & Risk Engine",
    desc: "Real-time transaction fraud detection processing 100K+ events per second — ML models for anomaly detection, device fingerprinting, velocity checks, and adaptive rules with 99.4%+ precision.",
    tags: ["ML Models", "Real-time", "XGBoost", "Kafka"],
    color: "#DC2626",
  },
  {
    icon: "📊",
    title: "Lending & Credit Platform",
    desc: "End-to-end digital lending platforms — loan origination, credit scoring with bureau integrations (CIBIL, Experian), automated underwriting, EMI scheduling, collections, and NPA management.",
    tags: ["Credit Scoring", "CIBIL", "Underwriting", "Collections"],
    color: "#059669",
  },
  {
    icon: "📱",
    title: "Wealth Management & Trading App",
    desc: "Investment platforms with stock, mutual fund, and crypto portfolio management, robo-advisory, SIP automation, real-time market data feeds, and SEBI-compliant reporting.",
    tags: ["SEBI Compliant", "NSE/BSE APIs", "Robo-Advisory", "SIP"],
    color: "#7C3AED",
  },
  {
    icon: "🔄",
    title: "RegTech & Compliance Automation",
    desc: "Automated KYC/KYB pipelines with Aadhaar/DigiLocker verification, AML screening, FATF compliance, RBI reporting automation, and audit-ready transaction monitoring.",
    tags: ["KYC/KYB", "AML", "DigiLocker", "RBI Reports"],
    color: "#D97706",
  },
  {
    icon: "🏪",
    title: "Insurance Technology (InsurTech)",
    desc: "Digital insurance platforms — policy issuance, claims automation, underwriting engines, IRDAI-compliant reporting, embedded insurance APIs, and customer self-service portals.",
    tags: ["IRDAI", "Claims Automation", "Embedded Insurance", "Policy API"],
    color: "#0D9488",
  },
  {
    icon: "🔐",
    title: "FinTech Security & Compliance",
    desc: "PCI-DSS audits, SOC 2 Type II certification support, end-to-end encryption, HSM key management, penetration testing, and RBI IT framework compliance for financial institutions.",
    tags: ["PCI-DSS", "SOC 2", "HSM", "Pen Testing"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    items: [
      { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg", color: "#DD0031" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & AI",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Java Spring", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/spring/spring-original.svg", color: "#6DB33F" },
      { name: "Kafka", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apachekafka/apachekafka-original.svg", color: "#231F20" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "gRPC", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#244C5A" },
    ],
  },
  {
    category: "Database & Storage",
    items: [
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "Apache Spark", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apachespark/apachespark-original.svg", color: "#E25A1C" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
      { name: "TimescaleDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#FDB515" },
    ],
  },
  {
    category: "Cloud & Security",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "Terraform", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg", color: "#7B42BC" },
      { name: "Vault", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg", color: "#FFD814" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Regulatory Discovery & Compliance Mapping", desc: "We start by mapping your target market's regulatory landscape — RBI, SEBI, IRDAI, PCI-DSS, FATF — identifying licensing requirements, compliance obligations, and technical mandates before designing a single screen.", color: "#4F46E5" },
  { n: "02", icon: "🎨", title: "UX Research & Financial Product Design", desc: "User research with real banking customers, traders, and finance professionals. Trust-first UX design — reducing friction in KYC, checkout, and onboarding flows while meeting accessibility and security standards.", color: "#0891B2" },
  { n: "03", icon: "⚙️", title: "Agile Development with Financial QA", desc: "2-week sprints with dedicated financial QA — transaction integrity testing, race condition handling, ledger reconciliation validation, and edge case simulation alongside automated test suites.", color: "#7C3AED" },
  { n: "04", icon: "🔗", title: "Integrations & Open Banking APIs", desc: "Banking core system integrations, payment rail connections (UPI, NEFT, IMPS), bureau APIs (CIBIL, Experian), NACH/eMandate, GST APIs, account aggregator frameworks, and RBI regulatory reporting.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "Security Audit, UAT & Regulatory Sign-Off", desc: "Penetration testing, PCI-DSS audit support, load testing at 10× peak volume, regulatory sandbox testing, and a structured UAT phase with real users and compliance reviewers before go-live.", color: "#DC2626" },
  { n: "06", icon: "🛡️", title: "Monitoring, Incident Response & Growth", desc: "99.99% uptime SLA, real-time fraud alert monitoring, monthly PCI compliance reviews, RBI audit log reporting, automated failover, and continuous feature iteration driven by transaction analytics.", color: "#059669" },
];

const CASE_STUDIES = [
  {
    title: "PaySwift — Neobank for 500K Users",
    industry: "Digital Banking",
    desc: "Built a full-stack RBI-compliant neobank from scratch — KYC onboarding, multi-currency wallet, UPI/IMPS/NEFT payments, AI spend insights, and a credit line product. Onboarded 500,000 users in 8 months with 99.99% payment success rate.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["React Native", "Node.js", "Kafka", "AWS"],
    metrics: [{ val: "500K+", lbl: "Active Users" }, { val: "99.99%", lbl: "Payment Success" }, { val: "₹420Cr+", lbl: "Monthly GMV" }],
    color: "#4F46E5",
  },
  {
    title: "ShieldAI — Real-Time Fraud Detection",
    industry: "Fraud Prevention",
    desc: "A real-time fraud detection engine for a leading NBFC processing 80,000 transactions per second — ML-powered anomaly detection, device fingerprinting, and behavioural analytics that cut fraud losses by 94% in the first quarter.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop&auto=format",
    tags: ["Python", "XGBoost", "Kafka", "Redis"],
    metrics: [{ val: "94%", lbl: "Fraud Reduction" }, { val: "80K+", lbl: "TPS Processed" }, { val: "₹18Cr", lbl: "Losses Prevented" }],
    color: "#DC2626",
  },
  {
    title: "CapitalEdge — Lending & Credit SaaS",
    industry: "Digital Lending",
    desc: "An end-to-end digital lending platform for a mid-market NBFC — automated CIBIL/Experian credit scoring, instant loan origination, NACH e-mandate, EMI scheduling, and a collections dashboard. Reduced loan processing time from 7 days to 4 hours.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop&auto=format",
    tags: ["Java Spring", "PostgreSQL", "CIBIL API", "AWS"],
    metrics: [{ val: "4hrs", lbl: "Loan Processing" }, { val: "₹1,200Cr", lbl: "Loans Disbursed" }, { val: "0.3%", lbl: "NPA Rate" }],
    color: "#059669",
  },
];

const WHY_US = [
  { icon: "🏦", title: "Deep FinTech Domain Expertise", desc: "Our team includes ex-banking engineers, RBI sandbox participants, and compliance specialists who have built core banking systems, payment rails, and lending platforms from the ground up — not just integrated APIs.", color: "#4F46E5" },
  { icon: "🔒", title: "PCI-DSS, RBI & SEBI Ready", desc: "Every financial product we build is compliant by default — PCI-DSS Level 1, RBI IT framework, SEBI cybersecurity circular compliance, AML/CFT controls, SOC 2 Type II support, and full audit documentation.", color: "#0891B2" },
  { icon: "⚡", title: "High-Frequency Transaction Architecture", desc: "We architect for financial-grade performance — event-sourced ledgers, idempotent payment APIs, distributed saga patterns, and load-tested systems that handle 100K+ TPS without data integrity compromise.", color: "#7C3AED" },
  { icon: "🤖", title: "AI Risk Models That Work in Production", desc: "Our fraud detection and credit scoring models are trained on real financial datasets, validated by domain experts, monitored for drift, and updated continuously — not one-time proof-of-concept demos.", color: "#DC2626" },
  { icon: "🔗", title: "Payment Rail & Bureau Integrations", desc: "UPI, NACH, NEFT, RTGS, IMPS, Razorpay, Cashfree, CIBIL, Experian, Equifax, DigiLocker, Aadhaar, Account Aggregator, GST APIs — we've built and maintained all of them, and know every undocumented edge case.", color: "#D97706" },
  { icon: "🌐", title: "Regulatory Sandbox & Go-to-Market", desc: "We've helped fintech startups navigate RBI's regulatory sandbox, apply for Payment Aggregator licenses, structure NBFC partnerships, and design compliant products — from idea to regulatory approval.", color: "#059669" },
];

const FAQS = [
  {
    q: "Is the FinTech software you build PCI-DSS and RBI compliant?",
    a: "Yes — compliance is architected into the system from day one. For payment products, we implement PCI-DSS Level 1 controls: no card data stored in our systems (tokenisation via Stripe/Razorpay), encrypted transmission, network segmentation, and quarterly ASV scans. For RBI compliance, we follow the Master Direction on IT, RBI's cybersecurity framework for banks/NBFCs, and data localisation requirements. We produce compliance documentation, audit logs, and support your annual PCI-DSS QSA assessments.",
  },
  {
    q: "Can you integrate with UPI, NACH, banking cores, and credit bureaus?",
    a: "Absolutely. We have production integrations with all major Indian payment rails — UPI (via NPCI-certified banks), NEFT/RTGS/IMPS, NACH/eMandate for recurring payments, and Razorpay, Cashfree, and PayU as payment aggregators. For credit, we integrate with CIBIL, Experian, Equifax, and CRIF bureaus. We also connect to Aadhaar e-KYC, DigiLocker, Account Aggregator (AA) framework, and major core banking systems (Finacle, BankWare, FinnOne). If your core isn't on this list, we build custom adapters.",
  },
  {
    q: "How long does it take to build a neobank or lending platform?",
    a: "A focused MVP neobank (KYC onboarding, wallet, UPI payments, basic dashboard) takes 14–18 weeks. A full-featured digital lending platform (credit scoring, loan origination, NACH, collections) takes 5–8 months. An end-to-end payment gateway takes 10–16 weeks. We always start with a 2-week regulatory and technical discovery sprint that produces a detailed scope, compliance checklist, and fixed-price proposal. Regulatory sandbox participation adds 4–8 weeks depending on the RBI queue.",
  },
  {
    q: "How do you handle financial data security and prevent breaches?",
    a: "Financial data security is layered and non-negotiable. We implement HSM-based key management for cryptographic operations, AES-256 encryption at rest, TLS 1.3 in transit, field-level encryption for PAN/Aadhaar data, and strict RBAC with MFA for all admin access. Every deployment goes through OWASP Top 10 remediation and penetration testing by a CERT-In empanelled vendor. We also implement anomaly detection on admin actions and maintain immutable audit logs in tamper-proof storage.",
  },
  {
    q: "Can you build AI models for credit scoring and fraud detection?",
    a: "Yes. Our financial ML team builds credit scoring models using bureau data, alternative data (UPI transaction history, GST filings, bank statements), and behavioural features — validated against your historical portfolio. For fraud, we build real-time models using XGBoost, LightGBM, and deep learning ensembles, with rule engines for immediate blocking and ML for pattern detection. All models go through fairness audits, are monitored for performance drift, and are retrained on a scheduled cadence with your approval.",
  },
  {
    q: "What post-launch support do you offer for financial platforms?",
    a: "Financial systems demand the highest support standards because every minute of downtime is a direct revenue and compliance risk. We offer Standard (business hours, 4hr critical SLA), Financial (extended hours, 1hr critical SLA), and Mission-Critical (24/7, 15min critical SLA with dedicated on-call engineers) tiers. All tiers include real-time payment success rate monitoring, automatic incident escalation, monthly PCI compliance reviews, quarterly penetration tests, and a named account manager who understands financial operations.",
  },
];

const STATS = [
  { val: "160+", lbl: "FinTech Projects Delivered", icon: "💳" },
  { val: "₹5,000Cr+", lbl: "Transactions Processed", icon: "💰" },
  { val: "99.99%", lbl: "Payment Uptime SLA", icon: "⚡" },
  { val: "PCI-DSS", lbl: "Level 1 Compliant", icon: "🔒" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #E0E7FF" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 28px rgba(79,70,229,.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#1E1B4B,#4F46E5,#0891B2)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => (
            <div key={link.label} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {link.link ? (
                <a href={link.link} style={{
                  background: currentPath === link.link ? "#EEF2FF" : "none",
                  border: currentPath === link.link ? "1px solid #C7D2FE" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: currentPath === link.link ? "#4F46E5" : "#374151",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none",
                  transition: "all .2s",
                }}>{link.label}</a>
              ) : (
                <button style={{
                  background: link.label === "Industries" ? "#EEF2FF" : "none",
                  border: link.label === "Industries" ? "1px solid #C7D2FE" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: link.label === "Industries" ? "#4F46E5" : activeDropdown === link.label ? "#4F46E5" : "#374151",
                  display: "flex", alignItems: "center", gap: 4, transition: "all .2s",
                  fontFamily: "'Sora',sans-serif", borderRadius: 8,
                }}>
                  {link.label}
                  {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>}
                </button>
              )}
              {link.dropdown && activeDropdown === link.label && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 200,
                  background: "#fff", border: "1px solid #E0E7FF", borderRadius: 16,
                  boxShadow: "0 20px 60px rgba(79,70,229,.1)", minWidth: 230,
                  padding: "8px 0", animation: "fadeIn .2s ease",
                }}>
                  {link.dropdown.map(item => (
                    <a key={item.name} href={item.link} style={{
                      display: "block", padding: "10px 20px", fontSize: 13.5,
                      color: item.name === "FinTech" ? "#4F46E5" : "#374151",
                      background: item.name === "FinTech" ? "#EEF2FF" : "none",
                      fontWeight: item.name === "FinTech" ? 700 : 500,
                      textDecoration: "none", transition: "all .15s",
                      fontFamily: "'Sora',sans-serif",
                    }}
                      onMouseEnter={e => { e.target.style.background = "#EEF2FF"; e.target.style.color = "#4F46E5"; }}
                      onMouseLeave={e => {
                        e.target.style.background = item.name === "FinTech" ? "#EEF2FF" : "none";
                        e.target.style.color = item.name === "FinTech" ? "#4F46E5" : "#374151";
                      }}
                    >{item.name}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E1B4B,#4F46E5)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(79,70,229,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(79,70,229,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(79,70,229,.3)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#EEF2FF" : "none",
            border: "1px solid #E0E7FF", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>{mobileOpen ? "✕" : "☰"}</button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #E0E7FF",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,.1)",
        }}>
          
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)} style={{
                      width: "100%", textAlign: "left", padding: "13px 24px",
                      background: mobileDropdown === link.label ? "#EEF2FF" : "none",
                      border: "none", borderBottom: "1px solid #EEF2FF",
                      color: mobileDropdown === link.label ? "#4F46E5" : "#374151",
                      fontWeight: 600, fontSize: 15, fontFamily: "'Sora',sans-serif",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      {link.label}
                      <span style={{ fontSize: 11, color: "#9CA3AF", transition: "transform .2s", transform: mobileDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#F5F7FF", borderBottom: "1px solid #EEF2FF" }}>
                        {link.dropdown.map(item => (
                          <a key={item.name} href={item.link} onClick={() => setMobileOpen(false)} style={{
                            display: "block", padding: "11px 24px 11px 40px", fontSize: 14,
                            color: item.name === "FinTech" ? "#4F46E5" : "#4B5563",
                            background: item.name === "FinTech" ? "#EEF2FF" : "none",
                            fontWeight: item.name === "FinTech" ? 700 : 500,
                            textDecoration: "none", borderBottom: "1px solid #EEF2FF",
                            fontFamily: "'Sora',sans-serif",
                          }}>→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={link.link || "#"} onClick={() => setMobileOpen(false)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "13px 24px",
                    color: currentPath === link.link ? "#4F46E5" : "#374151",
                    background: currentPath === link.link ? "#EEF2FF" : "none",
                    fontWeight: 600, fontSize: 15, textDecoration: "none",
                    borderBottom: "1px solid #EEF2FF", fontFamily: "'Sora',sans-serif",
                  }}>
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a href="#contact" onClick={() => setMobileOpen(false)} style={{
              background: "linear-gradient(135deg,#1E1B4B,#4F46E5)", color: "#fff",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", boxShadow: "0 6px 20px rgba(79,70,229,.3)",
            }}>💳 Get Free Consultation</a>
            <a href="tel:+919646206032" style={{
              background: "#EEF2FF", color: "#1E1B4B",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", border: "1px solid #C7D2FE",
            }}>📞 Call: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["PCI-DSS Level 1 Compliant", "99.99% Payment Uptime", "₹5,000Cr+ Transactions Processed", "160+ FinTech Projects Delivered"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(155deg,#EEF2FF 0%,#F0F9FF 35%,#F5F3FF 65%,#F8FAFC 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(79,70,229,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(79,70,229,.05) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Curved blobs */}
      <div style={{ position: "absolute", top: -180, right: -120, width: 700, height: 700, borderRadius: "61% 39% 70% 30%/30% 50% 50% 70%", background: "radial-gradient(circle,rgba(79,70,229,.1),rgba(8,145,178,.05) 50%,transparent 75%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -80, width: 560, height: 560, borderRadius: "39% 61% 30% 70%/70% 30% 70% 30%", background: "radial-gradient(circle,rgba(124,58,237,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(8,145,178,.06),transparent 70%)", pointerEvents: "none" }} />

      {/* Decorative currency symbols */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { x: "6%", y: "18%", sym: "₹" },
          { x: "91%", y: "14%", sym: "$" },
          { x: "14%", y: "78%", sym: "%" },
          { x: "88%", y: "72%", sym: "€" },
          { x: "52%", y: "90%", sym: "₿" },
        ].map((item, i) => (
          <div key={i} style={{
            position: "absolute", left: item.x, top: item.y,
            fontSize: 22, fontWeight: 900,
            color: "#4F46E5",
            opacity: 0.07 + i * 0.02,
            fontFamily: "'Sora',sans-serif",
            animation: `ftPulse ${3 + i}s ease-in-out infinite ${i * 0.6}s`,
          }}>{item.sym}</div>
        ))}
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#9CA3AF" }}>
          <a href="/" style={{ color: "#4F46E5", textDecoration: "none", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Home</a>
          <span>›</span>
          <span style={{ fontFamily: "'Sora',sans-serif" }}>Industries</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>FinTech</span>
        </div>

        <div className="ft-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#4F46E5", display: "inline-block", animation: "ftPulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1E1B4B", fontFamily: "'Sora',sans-serif" }}>💳 India's Trusted FinTech Technology Partner</span>
            </div>

            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(36px,4.8vw,64px)",
              fontWeight: 900, color: "#1E1B4B",
              lineHeight: 1.1, marginBottom: 20,
            }}>
              FinTech Software<br />
              <span style={{ background: "linear-gradient(135deg,#4F46E5,#0891B2,#7C3AED)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Built for Trust,
              </span><br />
              Engineered for Scale.
            </h1>

            <p style={{ fontSize: 17, color: "#4B5563", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'Sora',sans-serif", fontWeight: 400 }}>
              We build PCI-DSS & RBI-compliant digital banking platforms, payment gateways, AI fraud detection engines, lending systems, and wealth management apps — engineered for the regulatory rigour and performance demands of modern finance.
            </p>

            {/* Rotating metric pill */}
            <div style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 12, padding: "13px 20px", marginBottom: 36, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#1E1B4B" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#1E1B4B,#4F46E5)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(79,70,229,.35)",
                transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(79,70,229,.48)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(79,70,229,.35)"; }}
              >💳 Start FinTech Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#1E1B4B",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "2px solid #C7D2FE",
                transition: "all .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#818CF8"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
              >📂 View Case Studies</a>
            </div>

            {/* Trust signal row */}
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
              {[["160+", "FinTech Projects"], ["₹5,000Cr+", "Transactions"], ["99.99%", "Uptime SLA"], ["PCI-DSS", "Certified"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#4F46E5" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", fontWeight: 600, marginTop: 2, fontFamily: "'Sora',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual panel */}
          <div style={{ position: "relative" }}>
            {/* Main image card */}
            <div style={{
              borderRadius: 28, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(79,70,229,.14), 0 0 0 1px rgba(79,70,229,.08)",
              background: "#fff", position: "relative",
            }}>
              {/* Dashboard chrome bar */}
              <div style={{ background: "#F5F7FF", borderBottom: "1px solid #E0E7FF", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#EEF2FF", borderRadius: 8, padding: "5px 14px", fontSize: 12, color: "#4F46E5", fontWeight: 600, margin: "0 12px", fontFamily: "'Sora',sans-serif" }}>
                  🔒 payswift-banking.techvanto.com
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, fontFamily: "'Sora',sans-serif", background: "#EEF2FF", padding: "4px 10px", borderRadius: 99, border: "1px solid #C7D2FE" }}>LIVE</div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=640&h=380&fit=crop&auto=format"
                alt="FinTech Dashboard"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
              {/* Overlay gradient */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100, background: "linear-gradient(to top,rgba(30,27,75,.3),transparent)" }} />
            </div>

            {/* Floating badge — PCI-DSS */}
            <div style={{ position: "absolute", top: -18, left: -22, background: "#fff", borderRadius: 18, padding: "14px 18px", boxShadow: "0 12px 40px rgba(79,70,229,.18)", border: "1.5px solid #E0E7FF", animation: "float 3.2s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#4F46E5,#0891B2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔒</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#1E1B4B" }}>PCI-DSS</div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>Level 1 Certified</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Uptime */}
            <div style={{ position: "absolute", bottom: 20, right: -22, background: "linear-gradient(135deg,#1E1B4B,#4F46E5)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(79,70,229,.4)", animation: "float 3.8s ease-in-out infinite .6s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#fff" }}>99.99%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Payment Uptime ⚡</div>
            </div>

            {/* Floating badge — Fraud */}
            <div style={{ position: "absolute", bottom: -18, left: 16, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.09)", border: "1.5px solid #FEF3C7", animation: "float 4.2s ease-in-out infinite 1.2s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>94% Fraud Reduction</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>AI detection engine</div>
                </div>
              </div>
            </div>

            {/* Floating badge — GMV */}
            <div style={{ position: "absolute", top: 56, right: -20, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 6px 22px rgba(79,70,229,.12)", border: "1.5px solid #E0E7FF", animation: "float 4.6s ease-in-out infinite 1.8s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>💰</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1E1B4B", fontFamily: "'Sora',sans-serif" }}>₹5,000Cr+</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Transactions Processed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#1E1B4B", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }} className="ft-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(28px,3vw,44px)", fontWeight: 900, color: "#fff",
              lineHeight: 1,
            }}>{s.val}</div>
            <div style={{ color: "#A5B4FC", fontSize: 13, marginTop: 8, fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="ft-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4F46E5", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>FinTech Software Solutions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 580, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>
            Regulatory-grade financial software built for the complexity of modern money — from a single-founder neobank to a 10-million-user payment platform.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#EEF2FF"}`,
                boxShadow: hov === i ? `0 28px 72px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden", cursor: "pointer",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}20` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#1E1B4B", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'Sora',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Sora',sans-serif" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F5F7FF" }} className="ft-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4F46E5", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>Financial Depth Meets Engineering Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 540, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>What separates a generic IT company from a true FinTech technology partner.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {WHY_US.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#E0E7FF"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}16` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: `${p.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1.5px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1E1B4B", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, fontFamily: "'Sora',sans-serif" }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="ft-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0891B2", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>Financial-Grade Technology Stack</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>The frameworks, tools, and platforms powering our FinTech solutions — chosen for reliability, security, and throughput.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#1E1B4B,#4F46E5)" : "#fff",
              borderColor: active === i ? "#4F46E5" : "#E0E7FF",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(79,70,229,.28)" : "none",
            }}>{t.category}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#EEF2FF"}`,
                background: hov === i ? `${tech.color}07` : "#F8F9FF",
                boxShadow: hov === i ? `0 14px 42px ${tech.color}22` : "0 1px 6px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#E0E7FF"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}20` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'Sora',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(155deg,#EEF2FF 0%,#F0F9FF 50%,#F5F3FF 100%)" }} className="ft-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>How We Build FinTech Software</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>A financial-grade development process — designed around regulatory compliance, transaction integrity, and real-world financial workflows.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 24, padding: "34px 30px",
              border: "1.5px solid #E0E7FF",
              boxShadow: "0 4px 20px rgba(79,70,229,.06)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = step.color; e.currentTarget.style.boxShadow = `0 20px 56px ${step.color}16`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E0E7FF"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(79,70,229,.06)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}10`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}22` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}12`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Sora',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1E1B4B", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="ft-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>FinTech Products We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>Production financial systems handling real money for real users — with the numbers that matter in finance.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="ft-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}1E` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cs.color}44,transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 16px", borderRadius: 99, fontFamily: "'Sora',sans-serif" }}>{cs.industry}</div>
              </div>

              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,40px)", fontWeight: 900, color: "#1E1B4B", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'Sora',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0E`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 36, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 28, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'Sora',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}30`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}30`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 68 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#4F46E5", border: "2px solid #C7D2FE",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#818CF8"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
          >View All FinTech Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F5F7FF" }} className="ft-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1E1B4B", marginTop: 10, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'Sora',sans-serif" }}>Everything you need to know before starting your FinTech technology project.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#4F46E5" : "#E0E7FF"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(79,70,229,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 600, fontSize: 15.5, color: "#1E1B4B", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#1E1B4B,#4F46E5)" : "#EEF2FF",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#4F46E5", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #EEF2FF", paddingTop: 18, fontFamily: "'Sora',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function FintechSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes ftPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.45; transform:scale(0.96); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .ft-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .ft-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; text-align:center; }
        .ft-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .ft-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .ft-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .ft-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .ft-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .ft-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .ft-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .ft-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .ft-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .ft-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Navbar />
      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}
